import Title from '@cohort/components-xps/components/Title';
import type {Currency} from '@cohort/shared/schema/common/currency';
import {CurrencySpecs} from '@cohort/shared/schema/common/currency';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {BaseModal} from '@cohort/wallet/components/modals/BaseModal';
import DrawerModal from '@cohort/wallet/components/modals/DrawerModal';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {CheckCircle, Info} from '@phosphor-icons/react';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

interface CurrencyItem {
  currency: Currency;
  selected: boolean;
  onSelect: () => void;
}

const CurrencyItem: React.FC<CurrencyItem> = ({currency, selected, onSelect}) => {
  const {accentColor} = useThemeContext();
  const currencySpec = CurrencySpecs.getOrThrow(currency);

  return (
    <SecondaryButton
      testId="select-currency"
      tracking={{
        namespace: 'store.buy.selectCurrencyModal.currencyItem',
      }}
      text={
        <div className={'flex w-full flex-row justify-between p-2 text-lg font-semibold'}>
          <span className="my-auto">{`${currencySpec.name} - ${currencySpec.symbol}`}</span>
          {selected && <CheckCircle className="h-8 w-8" style={{color: accentColor}} />}
        </div>
      }
      onClick={onSelect}
      disabled={selected}
      className={classNames('rounded-lg', selected && 'hover:cursor-default')}
      style={{borderColor: selected ? accentColor : 'transparent', borderWidth: '2px'}}
    />
  );
};

type SelectCurrencyProps = {
  currentCurrency: Currency;
  availableCurrencies: Currency[];
  setCurrency: (currency: Currency) => void;
  setModalOpened: (open: boolean) => void;
};

const SelectCurrencyModal: React.FC<SelectCurrencyProps> = ({
  currentCurrency,
  setCurrency,
  availableCurrencies,
  setModalOpened,
}) => {
  const {isMobile} = useScreenSizeContext();
  const {t} = useTranslation('components', {keyPrefix: 'modals.store.selectCurrencyModal'});

  const ModalContent = (
    <div className="flex w-full flex-col space-y-4">
      {availableCurrencies.sort().map(availableCurrency => (
        <CurrencyItem
          key={availableCurrency}
          currency={availableCurrency}
          selected={currentCurrency === availableCurrency}
          onSelect={() => setCurrency(availableCurrency)}
        />
      ))}
      <PrimaryButton
        testId="select-currency-continue"
        tracking={{
          namespace: 'store.buy.selectCurrencyModal.continue',
        }}
        text={t('continue')}
        onClick={() => setModalOpened(false)}
        className="rounded-lg"
      />
    </div>
  );

  const ModalComponent = isMobile ? DrawerModal : BaseModal;

  return (
    <ModalComponent
      title={
        <Title
          content={
            <Fragment>
              <Info className="h-8 w-8 text-slate-700" />
              <p className="text-lg">{t('title')}</p>
            </Fragment>
          }
          classes="flex flex-col items-center"
        />
      }
      tracking={{namespace: 'modals.perks.detail.selectDigitalAsset'}}
      onClose={() => setModalOpened(false)}
    >
      {ModalContent}
    </ModalComponent>
  );
};

export default SelectCurrencyModal;
